import React, { useState, useEffect } from 'react'
import { 
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'
import { getApiEndpoint } from '../../lib/api'
import toast from 'react-hot-toast'
import authAPI from '../../lib/authAPI'

const ModulePermissionMatrix = ({ onUserSelect }) => {
  const [data, setData] = useState({ modules: [], users: [], permissions: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [collapsedCategories, setCollapsedCategories] = useState({})

  useEffect(() => {
    fetchPermissionData()
  }, [])

  const fetchPermissionData = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(getApiEndpoint('ADMIN_MODULE_PERMISSIONS'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authAPI.token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const responseData = await response.json()
      
      if (responseData.error) {
        throw new Error(responseData.message || responseData.error)
      }

      setData({
        modules: responseData.modules || [],
        users: responseData.users || [],
        permissions: responseData.permissions || []
      })

    } catch (error) {
      console.error('❌ Failed to fetch permission data:', error)
      setError(error.message)
      toast.error('Failed to load permission data')
    } finally {
      setLoading(false)
    }
  }

  const togglePermission = async (userId, moduleId, currentlyHasAccess) => {
    try {
      const endpoint = getApiEndpoint('ADMIN_MODULE_PERMISSIONS')
      const method = currentlyHasAccess ? 'DELETE' : 'POST'
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${authAPI.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          module_id: moduleId,
          notes: currentlyHasAccess ? 'Revoked via permission matrix' : 'Granted via permission matrix'
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      
      if (result.error) {
        throw new Error(result.message || result.error)
      }

      // Refresh data to show changes
      await fetchPermissionData()
      
      toast.success(currentlyHasAccess ? 'Permission revoked' : 'Permission granted')

    } catch (error) {
      console.error('❌ Failed to toggle permission:', error)
      toast.error(`Failed to ${currentlyHasAccess ? 'revoke' : 'grant'} permission`)
    }
  }

  const hasPermission = (userId, moduleId) => {
    return data.permissions.some(p => 
      p.user_id === userId && p.module_id === moduleId
    )
  }

  const getUserStatusBadge = (user) => {
    if (user.is_admin) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          Admin
        </span>
      )
    }

    switch (user.status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Active
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        )
    }
  }

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  // Group modules by category
  const modulesByCategory = data.modules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = []
    }
    acc[module.category].push(module)
    return acc
  }, {})

  const categoryOrder = ['assessment', 'reports', 'assets', 'services', 'tools', 'integrations', 'settings', 'admin']

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-sm text-gray-600">Loading permission matrix...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-red-900">Failed to load permissions</h3>
        <p className="mt-1 text-sm text-red-600">{error}</p>
        <button
          onClick={fetchPermissionData}
          className="mt-3 inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Module Permission Matrix</h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage user access to application modules. Click toggles to grant or revoke permissions.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                User
              </th>
              {categoryOrder.map(category => {
                const categoryModules = modulesByCategory[category] || []
                if (categoryModules.length === 0) return null
                
                return (
                  <th key={category} className="px-2 py-3 text-center">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="flex items-center justify-center w-full text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      <span className="mr-1">{category}</span>
                      {collapsedCategories[category] ? (
                        <ChevronDownIcon className="w-3 h-3" />
                      ) : (
                        <ChevronUpIcon className="w-3 h-3" />
                      )}
                    </button>
                    {!collapsedCategories[category] && (
                      <div className="flex mt-2 space-x-1">
                        {categoryModules.map(module => (
                          <div key={module.id} className="text-center min-w-[80px]">
                            <div className="text-xs text-gray-600 font-normal normal-case">
                              {module.display_name.length > 10 
                                ? `${module.display_name.substring(0, 10)}...` 
                                : module.display_name
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {data.users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10 border-r border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => onUserSelect && onUserSelect(user)}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left truncate block max-w-[200px]"
                        title={user.email}
                      >
                        {user.email}
                      </button>
                      <div className="mt-1">
                        {getUserStatusBadge(user)}
                      </div>
                    </div>
                  </div>
                </td>
                
                {categoryOrder.map(category => {
                  const categoryModules = modulesByCategory[category] || []
                  if (categoryModules.length === 0) return null
                  
                  return (
                    <td key={category} className="px-2 py-4">
                      {!collapsedCategories[category] && (
                        <div className="flex space-x-1 justify-center">
                          {categoryModules.map(module => {
                            const hasAccess = user.is_admin || hasPermission(user.id, module.id)
                            
                            return (
                              <button
                                key={module.id}
                                onClick={() => !user.is_admin && togglePermission(user.id, module.id, hasAccess)}
                                disabled={user.is_admin}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  user.is_admin 
                                    ? 'bg-purple-100 cursor-not-allowed'
                                    : hasAccess
                                      ? 'bg-green-100 hover:bg-green-200 text-green-600'
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-400'
                                }`}
                                title={`${module.display_name} - ${hasAccess ? 'Granted' : 'Not granted'}${user.is_admin ? ' (Admin has all access)' : ''}`}
                              >
                                {hasAccess ? (
                                  <CheckCircleIcon className="w-4 h-4" />
                                ) : (
                                  <XCircleIcon className="w-4 h-4" />
                                )}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.users.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No users found</p>
        </div>
      )}
    </div>
  )
}

export default ModulePermissionMatrix
